import React, {useEffect, useMemo, useState} from 'react'
import FormCompanyLogo from './FormCompanyLogo'
import * as XLSX from 'xlsx'
import {STATIC_FORMS, StaticFormConfig, StaticField} from './staticFormsConfig'
import {useAuth} from '../../../auth/core/Auth'
import {getVoyageList} from '../../../operations/core/_requests'
import {Vessel} from '../../core/_models'
import {Voyage} from '../../../operations/core/_models'
import {getAccessibleVessels, isVesselAssignedUserOnly} from '../../core/technicalScopeUtils'
import {
  fetchAccessibleVesselsForCurrentUser,
  normalizeAccessibleVessels,
} from '../../../../shared/access/accessibleVessels'
import {TECH_PERM} from '../../core/technicalPermissions'
import {
  TechnicalFormTemplate,
  uploadTechnicalFormSubmission,
  createTechnicalFormTemplate,
} from '../../core/forms_requests'
import {downloadOfflineExcel, buildOfflineExcelFile} from './offlineTemplate'
import SearchableSelect from '../../../../shared/components/SearchableSelect'
import PortSelect from '../../../../shared/components/PortSelect'
import { isPortField } from '../../../../shared/utils/portFieldUtils'

type ChecklistOption = 'yes' | 'no' | 'na'
type ChecklistValue = Record<string, ChecklistOption>

type FieldValue = string | ChecklistValue

type ParsedOffline = {
  formCode?: string
  remarks?: string
  values: Partial<Record<string, FieldValue>>
}

const normalizeText = (value: unknown) =>
  (value === null || value === undefined ? '' : String(value)).trim()

const normalizeMetaKey = (value: unknown) => normalizeText(value).toLowerCase().replace(/\s+/g, '')

const normalizeChecklistValue = (value: unknown): ChecklistOption | null => {
  const raw = normalizeText(value)
  if (!raw) return null
  const cleaned = raw.toLowerCase().replace(/\s+/g, '')
  if (['yes', 'y', 'true', '1'].includes(cleaned)) return 'yes'
  if (['na', 'n/a', 'notapplicable', 'notapplicable.'].includes(cleaned)) return 'na'
  if (['no', 'n', 'false', '0'].includes(cleaned)) return 'no'
  return 'no'
}

const pad2 = (value: number) => value.toString().padStart(2, '0')

const normalizeDateValue = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`
  }
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value)
    if (parsed?.y && parsed?.m && parsed?.d) {
      return `${parsed.y}-${pad2(parsed.m)}-${pad2(parsed.d)}`
    }
  }
  const text = normalizeText(value)
  return text || undefined
}

const normalizeTimeValue = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    return `${pad2(value.getHours())}:${pad2(value.getMinutes())}`
  }
  if (typeof value === 'number') {
    const totalSeconds = Math.round(value * 24 * 60 * 60)
    const hours = Math.floor(totalSeconds / 3600) % 24
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    return `${pad2(hours)}:${pad2(minutes)}`
  }
  const text = normalizeText(value)
  return text || undefined
}

const normalizeFieldValue = (field: StaticField, value: unknown): string | undefined => {
  if (field.type === 'date') return normalizeDateValue(value)
  if (field.type === 'time') return normalizeTimeValue(value)
  const text = normalizeText(value)
  return text || undefined
}

const isChecklistValue = (value: FieldValue | undefined): value is ChecklistValue =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const parseSingleSheet = (
  sheet: XLSX.WorkSheet,
  fieldMap: Map<string, StaticField>
): ParsedOffline | null => {
  const rows = XLSX.utils.sheet_to_json(sheet, {header: 1, defval: ''}) as unknown[][]
  const headerIndex = rows.findIndex((row) =>
    (row || []).some((cell) => normalizeText(cell).toLowerCase() === 'field key')
  )
  if (headerIndex === -1) return null

  const headerRow = rows[headerIndex] || []
  const header = headerRow.map((cell) => normalizeText(cell).toLowerCase())
  const fieldKeyIndex = header.indexOf('field key')
  const labelIndex = header.indexOf('label/item')
  const valueIndex = header.findIndex((cell) => cell.startsWith('value'))
  if (fieldKeyIndex === -1 || valueIndex === -1) return null

  const meta: Record<string, string> = {}
  for (let i = 0; i < headerIndex; i += 1) {
    const row = rows[i] || []
    const key = normalizeMetaKey(row[0])
    if (!key) continue
    const value = normalizeText(row[1])
    if (value) meta[key] = value
  }

  const values: Partial<Record<string, FieldValue>> = {}
  for (let i = headerIndex + 1; i < rows.length; i += 1) {
    const row = rows[i] || []
    const fieldKey = normalizeText(row[fieldKeyIndex])
    if (!fieldKey) continue
    const field = fieldMap.get(fieldKey)
    if (!field) continue

    if (field.type === 'checklist' && field.items) {
      const itemLabel = normalizeText(row[labelIndex])
      if (!itemLabel) continue
      const normalized = normalizeChecklistValue(row[valueIndex])
      if (!normalized) continue
      const current = (values[fieldKey] as ChecklistValue) || {}
      current[itemLabel] = normalized
      values[fieldKey] = current
      continue
    }

    const normalized = normalizeFieldValue(field, row[valueIndex])
    if (normalized === undefined) continue
    values[fieldKey] = normalized
  }

  return {
    formCode: meta.formcode,
    remarks: meta.remarks,
    values,
  }
}

const initValues = (form: StaticFormConfig): Record<string, FieldValue> => {
  const values: Record<string, FieldValue> = {}
  form.fields.forEach((f) => {
    if (f.type === 'checklist' && f.items) {
      const entry: ChecklistValue = {}
      f.items.forEach((item) => {
        entry[item] = 'no'
      })
      values[f.name] = entry
    } else {
      values[f.name] = ''
    }
  })
  return values
}

interface Props {
  templates: TechnicalFormTemplate[]
  refreshTemplates?: () => Promise<TechnicalFormTemplate[] | null | undefined>
  onTemplateCreated?: (template: TechnicalFormTemplate) => void
  onSubmissionUploaded?: () => void
  submissionContext?: {
    crewId?: number
    vesselId?: number
    companyId?: number
  }
  canSubmit?: boolean
}

const StaticFormRenderer: React.FC<Props> = ({
  templates,
  refreshTemplates,
  onTemplateCreated,
  onSubmissionUploaded,
  submissionContext,
  canSubmit = true,
}) => {
  const {currentUser, isSuperAdmin, hasPermission} = useAuth()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const crewVesselName = currentUser?.vessel?.fleet_name || ''
  const crewVesselId = currentUser?.vessel?.id
  const crewRank = currentUser?.rank?.rank || ''
  const crewName =
    currentUser?.roleEntityName ||
    currentUser?.username ||
    sessionStorage.getItem('roleEntityName') ||
    sessionStorage.getItem('userName') ||
    ''
  const companyAdminId = currentUser?.companyAdminId
  const companyGroupAdminId = currentUser?.companyGroupAdminId

  const [selectedCode, setSelectedCode] = useState(STATIC_FORMS[0]?.code || '')
  const [templateList, setTemplateList] = useState<TechnicalFormTemplate[]>(templates)
  const [values, setValues] = useState<Record<string, FieldValue>>(initValues(STATIC_FORMS[0]))
  const [remarks, setRemarks] = useState('')
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [provisioningTemplate, setProvisioningTemplate] = useState(false)
  const [offlineFile, setOfflineFile] = useState<File | null>(null)
  const [offlineUploading, setOfflineUploading] = useState(false)
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [voyages, setVoyages] = useState<Voyage[]>([])
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null)
  const [loadingVessels, setLoadingVessels] = useState(false)
  const [loadingVoyages, setLoadingVoyages] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  useEffect(() => {
    setTemplateList(templates)
  }, [templates])
  const normalizeCode = useMemo(
    () => (code?: string) => code ? code.toLowerCase().match(/[a-z0-9]+/g)?.[0] || '' : '',
    []
  )

  const selectedForm = useMemo(
    () => STATIC_FORMS.find((f) => f.code === selectedCode) || STATIC_FORMS[0],
    [selectedCode]
  )

  const findMatchingTemplate = (list = templateList) => {
    if (!list.length) return null
    const selectedCode = normalizeCode(selectedForm.code)
    const directMatch = list.find((t) => normalizeCode(t.code) === selectedCode)
    if (directMatch) return directMatch
    const rawCode = selectedForm.code.toLowerCase()
    return (
      list.find((t) => t.code?.toLowerCase().includes(rawCode)) ||
      list.find((t) => t.title?.toLowerCase().includes(rawCode)) ||
      null
    )
  }

  useEffect(() => {
    if (selectedForm) {
      const baseValues = initValues(selectedForm)
      if (
        isVesselOnlyUser &&
        crewVesselName &&
        Object.prototype.hasOwnProperty.call(baseValues, 'vesselName')
      ) {
        baseValues.vesselName = crewVesselName
      }
      if (
        isVesselOnlyUser &&
        crewRank &&
        Object.prototype.hasOwnProperty.call(baseValues, 'rank')
      ) {
        baseValues.rank = crewRank
      }
      if (
        isVesselOnlyUser &&
        crewName &&
        Object.prototype.hasOwnProperty.call(baseValues, 'masterName')
      ) {
        baseValues.masterName = crewName
      }
      if (
        isVesselOnlyUser &&
        crewName &&
        Object.prototype.hasOwnProperty.call(baseValues, 'master')
      ) {
        baseValues.master = crewName
      }
      setValues(baseValues)
      setRemarks('')
      setStatus(null)
      setError(null)
      setOfflineFile(null)
      if (isVesselOnlyUser) {
        setSelectedVesselId(crewVesselId ?? null)
      }
    }
  }, [crewName, crewRank, crewVesselId, crewVesselName, isVesselOnlyUser, selectedForm])

  useEffect(() => {
    if (isVesselOnlyUser) return
    let active = true
    setLoadingVessels(true)
    setLookupError(null)
    fetchAccessibleVesselsForCurrentUser()
      .then((list) => {
        if (!active) return
        setVessels(normalizeAccessibleVessels(list))
      })
      .catch((err: any) => {
        if (!active) return
        setLookupError('Unable to load vessels.')
      })
      .finally(() => {
        if (!active) return
        setLoadingVessels(false)
      })
    return () => {
      active = false
    }
  }, [isVesselOnlyUser])

  useEffect(() => {
    if (isVesselOnlyUser) return
    let active = true
    setLoadingVoyages(true)
    setLookupError(null)
    getVoyageList()
      .then((list) => {
        if (!active) return
        setVoyages(list)
      })
      .catch((err: any) => {
        if (!active) return
        setLookupError('Unable to load voyages.')
      })
      .finally(() => {
        if (!active) return
        setLoadingVoyages(false)
      })
    return () => {
      active = false
    }
  }, [isVesselOnlyUser])

  const filteredVessels = useMemo(() => {
    if (isVesselOnlyUser) return []
    return getAccessibleVessels(vessels, currentUser, isSuperAdmin)
  }, [currentUser, isSuperAdmin, isVesselOnlyUser, vessels])

  // Derive branding from the current form value, which also clears on a form switch/import.
  const logoVesselId = isVesselOnlyUser
    ? crewVesselId
    : filteredVessels.find((vessel) => vessel.fleet_name === values.vesselName)?.id

  const filteredVoyages = useMemo(() => {
    if (isVesselOnlyUser) return []
    let list = voyages
    if (companyAdminId) {
      list = list.filter((voyage) => voyage.companyAdminId === companyAdminId)
    } else if (companyGroupAdminId) {
      list = list.filter((voyage) => voyage.companyGroupAdminId === companyGroupAdminId)
    }
    if (selectedVesselId) {
      list = list.filter((voyage) => voyage.vessel?.id === selectedVesselId)
    }
    return list
  }, [companyAdminId, companyGroupAdminId, isVesselOnlyUser, selectedVesselId, voyages])
  const submitting = uploading || provisioningTemplate
  const offlineBusy = offlineUploading || provisioningTemplate

  const handleFieldChange = (field: StaticField, value: string) => {
    setValues((prev) => ({...prev, [field.name]: value}))
  }

  const setChecklistValue = (field: StaticField, item: string, next: ChecklistOption) => {
    setValues((prev) => {
      const current = (prev[field.name] as ChecklistValue) || {}
      const currentValue = current[item] || 'no'
      const resolved = currentValue === next ? 'no' : next
      return {
        ...prev,
        [field.name]: {...current, [item]: resolved},
      }
    })
  }

  const handleVesselChange = (field: StaticField, value: string) => {
    const match = filteredVessels.find((vessel) => vessel.fleet_name === value)
    setSelectedVesselId(match?.id ?? null)
    setValues((prev) => ({
      ...prev,
      [field.name]: value,
      ...(selectedForm?.fields.some((f) => f.name === 'voyageNo') ? {voyageNo: ''} : {}),
    }))
  }

  const asPlainObject = () => {
    const output: Record<string, any> = {}
    selectedForm.fields.forEach((f) => {
      const val = values[f.name]
      output[f.name] = val
    })
    return output
  }

  const buildFile = () => {
    const payload = {
      formCode: selectedForm.code,
      formTitle: selectedForm.title,
      submittedAt: new Date().toISOString(),
      data: asPlainObject(),
      remarks,
    }
    const json = JSON.stringify(payload, null, 2)
    return new File([json], `${selectedForm.code}-submission.json`, {type: 'application/json'})
  }

  const downloadFilled = () => {
    const file = buildFile()
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = file.name
    link.click()
    URL.revokeObjectURL(url)
  }

  const ensureTemplateAvailable = async (): Promise<TechnicalFormTemplate | null> => {
    let template = findMatchingTemplate()
    if (template) return template

    if (refreshTemplates) {
      try {
        const refreshed = await refreshTemplates()
        if (refreshed?.length) {
          setTemplateList(refreshed)
          template = findMatchingTemplate(refreshed)
          if (template) return template
        }
      } catch (err: any) {
        setError('Unable to load templates from server.')
        return null
      }
    }

    if (!hasPermission(TECH_PERM.CREATE_TECHNICAL_FORM)) {
      setError(
        'You do not have permission to create a form template. Please ask your administrator to provision this form.'
      )
      return null
    }

    try {
      setProvisioningTemplate(true)
      const offlineTemplateFile = buildOfflineExcelFile(selectedForm)
      const created = await createTechnicalFormTemplate({
        code: selectedForm.code,
        title: selectedForm.title,
        description: selectedForm.description,
        category: selectedForm.code.match(/[a-zA-Z]+/)?.[0],
        allowedFormats: ['json', 'xlsx', 'xls'],
        file: offlineTemplateFile,
        companyId: submissionContext?.companyId,
      })
      setTemplateList((prev) => [...prev, created])
      onTemplateCreated?.(created)
      return created
    } catch (err: any) {
      setError('Template not found on server. Please ask the office to upload the master template.')
      return null
    } finally {
      setProvisioningTemplate(false)
    }
  }

  const submitToServer = async () => {
    setError(null)
    setStatus(null)
    const template = await ensureTemplateAvailable()
    if (!template) return
    try {
      setUploading(true)
      const file = buildFile()
      const vesselVal = (values?.vesselName || values?.vessel || values?.shipName || '') as string
      const matchedVessel = filteredVessels.find(
        (v) => (v.fleet_name && v.fleet_name === vesselVal) || (v.name && v.name === vesselVal)
      )
      const resolvedVesselId = selectedVesselId || matchedVessel?.id || submissionContext?.vesselId
      await uploadTechnicalFormSubmission(template.id, {
        file,
        remarks,
        ...submissionContext,
        vesselId: resolvedVesselId,
        userId: currentUser?.id,
      })
      setStatus('Submitted to server')
      if (onSubmissionUploaded) onSubmissionUploaded()
    } catch (err: any) {
      setError('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const importOfflineFile = async () => {
    setError(null)
    setStatus(null)
    if (!offlineFile) {
      setError('Choose a filled Excel file to import.')
      return
    }
    try {
      setOfflineUploading(true)
      const buffer = await offlineFile.arrayBuffer()
      const workbook = XLSX.read(buffer, {type: 'array', cellDates: true})
      const sheetName = workbook.SheetNames[0]
      const sheet = sheetName ? workbook.Sheets[sheetName] : null
      if (!sheet) {
        setError('Unable to read Excel sheet.')
        return
      }
      const fieldMap = new Map(selectedForm.fields.map((field) => [field.name, field]))
      const parsed = parseSingleSheet(sheet, fieldMap)
      if (!parsed) {
        setError('Please use the latest single-sheet Excel template.')
        return
      }
      if (parsed.formCode && normalizeCode(parsed.formCode) !== normalizeCode(selectedForm.code)) {
        setError(`This Excel is for ${parsed.formCode}. Please open that form and try again.`)
        return
      }
      setValues((prev) => {
        const next = {...prev}
        Object.entries(parsed.values).forEach(([key, value]) => {
          if (isChecklistValue(value)) {
            const current = (next[key] as ChecklistValue) || {}
            next[key] = {...current, ...value}
            return
          }
          if (value !== undefined) {
            next[key] = value as string
          }
        })
        return next
      })
      if (parsed.remarks) {
        setRemarks(parsed.remarks)
      }
      setStatus('Excel loaded. Review the form and submit.')
      setOfflineFile(null)
    } catch (err: any) {
      setError('Unable to read Excel file.')
    } finally {
      setOfflineUploading(false)
    }
  }

  if (!selectedForm) return null

  return (
    <div className='card shadow-none border'>
      <div className='card-header border-0 py-4 d-flex justify-content-between align-items-center flex-wrap gap-3'>
        <div>
          <h3 className='card-title mb-1'>Static Digital Forms (first 5)</h3>
          <div className='text-muted small'>
            Fill directly in-app, or download a fillable Excel template for offline use.
          </div>
        </div>
        <SearchableSelect
          className='form-select w-auto'
          value={selectedForm.code}
          onChange={(e) => setSelectedCode(e.target.value)}
        >
          {STATIC_FORMS.map((f) => (
            <option key={f.code} value={f.code}>
              {`${f.code.toUpperCase()} - ${f.title}`}
            </option>
          ))}
        </SearchableSelect>
      </div>
      <div className='card-body' style={{position: 'relative'}}>
        <FormCompanyLogo vesselId={logoVesselId}>
        {(uploading || offlineUploading || provisioningTemplate) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className='d-flex align-items-center gap-2 text-muted'>
              <span className='spinner-border spinner-border-sm' />
              Processing...
            </div>
          </div>
        )}
        <div className='mb-3'>
          <div className='fw-semibold'>{selectedForm.title}</div>
          {selectedForm.description && (
            <div className='text-muted small'>{selectedForm.description}</div>
          )}
          {lookupError && <div className='text-danger small mt-1'>{lookupError}</div>}
        </div>

        <div className='row g-3'>
          {(() => {
            const fields = selectedForm.fields
            const result: typeof fields = []
            const validFromDate = fields.find((f) => f.name === 'validFromDate')
            const validFromTime = fields.find((f) => f.name === 'validFromTime')
            const validToDate = fields.find((f) => f.name === 'validToDate')
            const validToTime = fields.find((f) => f.name === 'validToTime')
            const dateField = fields.find((f) => f.name === 'date')
            const locationField = fields.find(
              (f) =>
                f.name === 'locationOfWork' ||
                f.name === 'enclosedSpaceName' ||
                f.name === 'workLocation' ||
                f.name === 'spaceDesignation'
            )

            const specialNames = new Set([
              'validFromDate',
              'validFromTime',
              'validToDate',
              'validToTime',
              'date',
              'locationOfWork',
              'enclosedSpaceName',
              'workLocation',
              'spaceDesignation',
            ])

            for (const field of fields) {
              if (specialNames.has(field.name)) {
                if (field.name === 'date' || (!dateField && field.name === locationField?.name)) {
                  if (dateField) result.push(dateField)
                  if (locationField) result.push(locationField)
                } else if (
                  field.name === 'validFromDate' ||
                  (!validFromDate && field.name === 'validFromTime')
                ) {
                  if (validFromDate) result.push(validFromDate)
                  if (validFromTime) result.push(validFromTime)
                } else if (
                  field.name === 'validToDate' ||
                  (!validToDate && field.name === 'validToTime')
                ) {
                  if (validToDate) result.push(validToDate)
                  if (validToTime) result.push(validToTime)
                }
              } else {
                result.push(field)
              }
            }
            return result
          })().map((field) => {
            const isVesselField = field.name === 'vesselName'
            const isVoyageField = field.name === 'voyageNo'
            const isCrewRankField = field.name === 'rank'
            const isMasterNameField = field.name === 'masterName' || field.name === 'master'
            const isPermitSeriesField = field.name === 'permitSeries'
            if (field.type === 'checklist' && field.items) {
              const checklist = (values[field.name] as ChecklistValue) || {}
              return (
                <div className='col-12' key={field.name}>
                  <div className='fw-semibold mb-2 text-body'>{field.label}</div>
                  <div className='d-flex flex-column gap-2'>
                    {field.items.map((item) => {
                      const current = checklist[item] || 'no'
                      const isYes = current === 'yes'
                      const isNa = current === 'na'
                      return (
                        <div className='d-flex flex-column flex-md-row gap-2' key={item}>
                          <div className='text-body flex-grow-1'>{item}</div>
                          <div className='d-flex align-items-center gap-3'>
                            <label className='form-check form-check-sm'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={isYes}
                                disabled={isNa}
                                onChange={() => setChecklistValue(field, item, 'yes')}
                              />
                              <span className='form-check-label text-body'>Yes</span>
                            </label>
                            <label className='form-check form-check-sm'>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={isNa}
                                disabled={isYes}
                                onChange={() => setChecklistValue(field, item, 'na')}
                              />
                              <span className='form-check-label text-body'>NA</span>
                            </label>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            }

            return (
              <div className='col-md-6' key={field.name}>
                <label className='form-label text-body'>
                  {field.label} {field.required ? '*' : ''}
                </label>
                {isVesselField && !isVesselOnlyUser ? (
                  <>
                    <SearchableSelect
                      className='form-select'
                      value={(values[field.name] as string) || ''}
                      onChange={(e) => handleVesselChange(field, e.target.value)}
                      disabled={loadingVessels}
                    >
                      <option value=''>Select vessel</option>
                      {filteredVessels.map((vessel) => (
                        <option key={vessel.id} value={vessel.fleet_name}>
                          {vessel.fleet_name}
                        </option>
                      ))}
                    </SearchableSelect>
                    {loadingVessels && (
                      <div className='text-muted small mt-1'>Loading vessels...</div>
                    )}
                    {!loadingVessels && filteredVessels.length === 0 && (
                      <div className='text-muted small mt-1'>No vessels available.</div>
                    )}
                  </>
                ) : isVesselField && isVesselOnlyUser ? (
                  <input
                    type='text'
                    className='form-control'
                    value={(values[field.name] as string) || ''}
                    readOnly
                  />
                ) : isPermitSeriesField ? (
                  <input
                    type='text'
                    className='form-control'
                    value={(values[field.name] as string) || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                  />
                ) : isVoyageField && !isVesselOnlyUser ? (
                  <>
                    <SearchableSelect
                      className='form-select'
                      value={(values[field.name] as string) || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      disabled={loadingVoyages}
                    >
                      <option value=''>Select voyage</option>
                      {filteredVoyages.map((voyage) => (
                        <option key={voyage.id} value={voyage.voyageNumber}>
                          {voyage.voyageNumber}
                          {voyage.departurePort && voyage.arrivalPort
                             ? ` - ${voyage.departurePort} to ${voyage.arrivalPort}`
                             : ''}
                        </option>
                      ))}
                    </SearchableSelect>
                    {loadingVoyages && (
                      <div className='text-muted small mt-1'>Loading voyages...</div>
                    )}
                    {!loadingVoyages && filteredVoyages.length === 0 && (
                      <div className='text-muted small mt-1'>No voyages available.</div>
                    )}
                  </>
                ) : isCrewRankField && isVesselOnlyUser ? (
                  <input
                    type='text'
                    className='form-control'
                    value={(values[field.name] as string) || ''}
                    readOnly
                  />
                ) : isMasterNameField && isVesselOnlyUser ? (
                  <input
                    type='text'
                    className='form-control'
                    value={(values[field.name] as string) || ''}
                    readOnly
                  />
                ) : field.type === 'textarea' ? (
                  <textarea
                    className='form-control'
                    rows={3}
                    value={(values[field.name] as string) || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                  />
                ) : field.type === 'select' && field.options ? (
                  <SearchableSelect
                    className='form-select'
                    value={(values[field.name] as string) || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                  >
                    <option value=''>Select...</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </SearchableSelect>
                ) : isPortField(field.label || field.name) ? (
                  <PortSelect
                    value={(values[field.name] as string) || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={`Select or search ${field.label.toLowerCase()}...`}
                  />
                ) : (
                  <input
                    type={field.type === 'date' || field.type === 'time' ? field.type : 'text'}
                    className='form-control'
                    value={(values[field.name] as string) || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                  />
                )}
                {field.helperText && (
                  <div className='text-muted small mt-1'>{field.helperText}</div>
                )}
              </div>
            )
          })}
        </div>

        <div className='mt-4'>
          <label className='form-label text-body'>Remarks (optional)</label>
          <textarea
            className='form-control'
            rows={3}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>

        <div className='d-flex flex-wrap gap-2 mt-4'>
          {/* <button className='btn btn-light' type='button' onClick={() => downloadOfflineExcel(selectedForm)}>
            Download fillable Excel
          </button> */}
          {canSubmit ? (
            <button
              className='btn btn-primary'
              type='button'
              onClick={submitToServer}
              disabled={submitting}
            >
              {
                submitting
                  ? provisioningTemplate
                    ? 'Preparing template...'
                    : 'Submitting...'
                  : 'Submit' /* was: 'Submit to server' */
              }
            </button>
          ) : (
            <span className='text-muted align-self-center'>Only crew can submit forms.</span>
          )}
        </div>

        {/* canSubmit ? (
          <div className='mt-4'>
            <label className='form-label text-body'>Import filled Excel</label>
            <input
              type='file'
              className='form-control'
              accept='.xlsx,.xls'
              onChange={(e) => setOfflineFile(e.target.files?.[0] || null)}
            />
            <button
              className='btn btn-secondary mt-3'
              type='button'
              onClick={importOfflineFile}
              disabled={offlineBusy}
            >
              {offlineBusy ? (provisioningTemplate ? 'Preparing template...' : 'Importing...') : 'Import Excel'}
            </button>
          </div>
        ) : null */}

        {provisioningTemplate && (
          <div className='text-muted mt-3'>Preparing template on server...</div>
        )}
        {status && <div className='text-success mt-3'>{status}</div>}
        {error && <div className='text-danger mt-3'>{error}</div>}
        </FormCompanyLogo>
      </div>
    </div>
  )
}

export default StaticFormRenderer
